import React, { useEffect, useRef } from "react";
import backgroundImage from "../assets/images/hero-bg.jpg";
import { Link } from "react-router-dom";

const HeroSection = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    // Add parallax effect
    const handleScroll = () => {
      if (heroRef.current) {
        const scrolled = window.pageYOffset;
        heroRef.current.style.transform = `translateY(${scrolled * 0.5}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      // Fallback to finding services section by class or data attribute
      const servicesElement = document.querySelector('.services-section, [data-section="services"]');
      if (servicesElement) {
        servicesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const serviceFeatures = [
    {
      icon: "💻",
      title: "Web Development",
      description: "Custom websites, e-commerce platforms, and web applications",
      price: "From $499",
      features: ["Responsive Design", "SEO Optimized", "Fast Loading"]
    },
    {
      icon: "📱",
      title: "Mobile Development",
      description: "iOS & Android apps, React Native, Flutter development",
      price: "From $799",
      features: ["Cross-platform", "User Friendly", "Push Notifications"]
    },
    {
      icon: "🎨",
      title: "UI/UX Design",
      description: "Beautiful interfaces, wireframes, and prototypes",
      price: "From $299",
      features: ["User Research", "Interactive Prototypes", "Design Systems"]
    },
    {
      icon: "📈",
      title: "Digital Marketing",
      description: "SEO, social media, and content marketing strategies",
      price: "From $399/mo",
      features: ["SEO Optimization", "Social Media", "Analytics Reports"]
    }
  ];

  return (
    <section className="relative w-full min-h-screen overflow-hidden bg-gray-900 flex items-center justify-center">
      {/* Background Image with Parallax */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <div ref={heroRef} className="w-full h-full">
          <img
            className="w-full h-full object-cover"
            src={backgroundImage}
            alt="Premium Digital Services"
          />
        </div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-1000"></div>
      </div>

      {/* Centered Content */}
      <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Badge */}
        <div className="inline-flex items-center bg-white/10 backdrop-blur-md rounded-full px-4 py-2 mb-8">
          <span className="relative flex h-3 w-3 mr-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-sm font-medium">Award-Winning Digital Agency</span>
        </div>

        {/* Main heading with animation */}
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 tracking-tight animate-fade-in-up">
          Transform Your
          <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent block mt-2">
            Digital Vision Into Reality
          </span>
        </h1>

        {/* Description */}
        <p className="text-lg md:text-xl mb-12 text-gray-200 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
          We craft exceptional digital experiences that drive growth, 
          engage users, and deliver measurable results for your business.
        </p>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 animate-fade-in-up animation-delay-400">
          {serviceFeatures.map((service, index) => (
            <div
              key={index}
              className="group bg-white/10 backdrop-blur-md rounded-xl p-6 hover:bg-white/20 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="text-5xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold mb-2">{service.title}</h3>
              <p className="text-sm text-gray-200 mb-3">{service.description}</p>
              <p className="text-green-400 font-bold text-lg mb-3">{service.price}</p>
              <div className="space-y-1">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center text-xs text-gray-300 gap-2">
                    <span className="text-green-400">✓</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Pricing Highlights */}
        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 mb-10 animate-fade-in-up animation-delay-600">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-gray-300 text-sm mb-1">Starting Price</p>
              <p className="text-3xl font-bold text-white">$499</p>
              <p className="text-xs text-gray-400">Basic Web Package</p>
            </div>
            <div className="text-center border-l border-r border-white/10">
              <p className="text-gray-300 text-sm mb-1">Most Popular</p>
              <p className="text-3xl font-bold text-green-400">$1,299</p>
              <p className="text-xs text-gray-400">Complete Business Solution</p>
            </div>
            <div className="text-center">
              <p className="text-gray-300 text-sm mb-1">Enterprise</p>
              <p className="text-3xl font-bold text-white">Custom</p>
              <p className="text-xs text-gray-400">Tailored to Your Needs</p>
            </div>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="flex flex-wrap justify-center gap-8 mb-10 animate-fade-in-up animation-delay-800">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-sm">100% Satisfaction Guarantee</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-sm">Free Consultation</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-sm">Lifetime Support</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-sm">Fast Turnaround</span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-1000">
          <button
            onClick={scrollToServices}
            className="group relative bg-gradient-to-r from-blue-500 to-purple-600 text-white px-10 py-4 rounded-lg font-semibold text-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 overflow-hidden cursor-pointer"
          >
            <span className="relative z-10">Explore Our Services</span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-700 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
          </button>
          
          <button
            onClick={scrollToContact}
            className="relative bg-white/10 backdrop-blur-md border-2 border-white text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105 group overflow-hidden cursor-pointer"
          >
            <span className="relative z-10">Get in Touch</span>
            <div className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
          </button>
        </div>

        {/* Trust Indicators with Counter */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center animate-fade-in-up animation-delay-1200">
          <div>
            <div className="text-3xl font-bold text-blue-400">150+</div>
            <div className="text-sm text-gray-300">Projects Completed</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-400">98%</div>
            <div className="text-sm text-gray-300">Client Satisfaction</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-400">24/7</div>
            <div className="text-sm text-gray-300">Support Available</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-400">5+</div>
            <div className="text-sm text-gray-300">Years Experience</div>
          </div>
        </div>
      </div>

      {/* Scroll Down Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 z-20">
        {/* Scroll to Services Indicator */}
        <button
          onClick={scrollToServices}
          className="text-white cursor-pointer group flex flex-col items-center"
          aria-label="Scroll to services"
        >
          <span className="text-xs mb-2 opacity-70 group-hover:opacity-100 transition-opacity">View Services</span>
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center group-hover:border-blue-400 transition-colors">
            <div className="w-1.5 h-3 bg-white rounded-full mt-2 animate-bounce group-hover:bg-blue-400 transition-colors"></div>
          </div>
        </button>

        {/* Separator Line */}
        <div className="w-px h-4 bg-white/30"></div>

        {/* Scroll to Contact Indicator */}
        <button
          onClick={scrollToContact}
          className="text-white cursor-pointer group flex flex-col items-center"
          aria-label="Scroll to contact"
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center group-hover:border-green-400 transition-colors">
            <div className="w-1.5 h-3 bg-white rounded-full mt-2 animate-bounce delay-500 group-hover:bg-green-400 transition-colors"></div>
          </div>
          <span className="text-xs mt-2 opacity-70 group-hover:opacity-100 transition-opacity">Contact Us</span>
        </button>
      </div>

      {/* Add animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(5px);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-bounce {
          animation: bounce 1.5s ease-in-out infinite;
        }
        
        .delay-500 {
          animation-delay: 0.5s;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        
        .animation-delay-600 {
          animation-delay: 0.6s;
        }
        
        .animation-delay-800 {
          animation-delay: 0.8s;
        }
        
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        
        .animation-delay-1200 {
          animation-delay: 1.2s;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;